import * as React from 'react';
import { ImageElement } from './ImageElement';
import type { ImageConfigComplete, ImageProps } from './types';
import { useImageConfig } from './Context/image-config-context';
import { getImgProps } from './utils/get-image-props';
import { cva } from 'class-variance-authority';

export const imageVariants = cva('rocco-image', {
    variants: {
        variant: {
            default: '',
        },
        multiplied: {
            true: 'mix-blend-multiply',
        },
        size: {
            professionalLogo: 'aspect-square h-[75%] w-[75%] object-contain',
            full: 'size-full',
            default: '',
        },
        fit: {
            cover: 'object-cover',
            contain: 'object-contain',
            default: '',
        },
        rounded: {
            full: 'rounded-full',
            default: '',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
        fit: 'default',
        rounded: 'default',
    },
});

export const placeholderVariants = cva('', {
    variants: {
        placeholderVariant: {
            light: 'bg-gray-200',
            dark: 'bg-gray-800',
            transparent: 'background-transparent',
        },
    },
    defaultVariants: {
        placeholderVariant: 'transparent',
    },
});

/**
 * An Image is a graphic representation of something.
 * It can be used to display images in a variety of ways.
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
    (props, forwardedRef) => {
        const {
            className,
            variant,
            placeholderVariant,
            multiplied,
            onLoad,
            size,
            fit,
        } = props;

        const imageConfigContext = useImageConfig();

        const config: ImageConfigComplete = React.useMemo(() => {
            if (!imageConfigContext) {
                throw new Error('Image component requires imageConfig');
            }

            const c = imageConfigContext;
            const {
                deviceSizes: configDeviceSizes = [],
                imageSizes: configImageSizes = [],
            } = c;

            // Only real dimensions join the ramp. `?? 0` put a literal 0 in it
            // for every image rendered without an explicit width or height,
            // which became a `width=0` CDN URL and a `0w` srcset candidate:
            // Chrome rejects the descriptor as invalid, logs it, and drops the
            // candidate — so it was a wasted, always-discarded entry pointing
            // at a zero-width image.
            const allSizes = [
                ...configDeviceSizes,
                ...configImageSizes,
                ...(props.width ? [props.width] : []),
                ...(props.height ? [props.height] : []),
            ].sort((a, b) => a - b);

            const deviceSizes = configDeviceSizes.sort((a, b) => a - b);

            return { ...c, allSizes: [...new Set(allSizes)], deviceSizes };
        }, [imageConfigContext, props.height, props.width]);

        const onLoadRef = React.useRef(onLoad);

        React.useEffect(() => {
            onLoadRef.current = onLoad;
        }, [onLoad]);

        const [placeholderComplete, setPlaceholderComplete] =
            React.useState(false);
        const [showAltText, setShowAltText] = React.useState(false);

        const imgPropsConfig = {
            imgConf: config,
            placeholderComplete,
        };

        const { props: imgAttributes, meta: imgMeta } = getImgProps(
            props,
            imageVariants({ variant, multiplied, size, fit, className }),
            placeholderVariants({ placeholderVariant }),
            imgPropsConfig,
        );

        return (
            <ImageElement
                {...imgAttributes}
                placeholder={imgMeta.placeholder}
                setPlaceholderComplete={setPlaceholderComplete}
                onLoadRef={onLoadRef}
                showAltText={showAltText}
                setShowAltText={setShowAltText}
                sizesInput={props.sizes}
                ref={forwardedRef}
            />
        );
    },
);

Image.displayName = 'Image';
